import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState({});  // Phase 19 — effective key → bool
  const [loading, setLoading] = useState(true);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/permissions");
      setPerms(data?.effective || {});
    } catch {
      setPerms({});
    }
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("st_token");
    if (!token) {
      setUser(null);
      setPerms({});
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("st_user", JSON.stringify(data));
      // Phase C — bootstrap the CSRF cookie so page refresh restores it.
      try { await api.get("/auth/csrf"); } catch (_e) { /* warn mode ok */ }
      await loadPerms();
    } catch {
      localStorage.removeItem("st_token");
      localStorage.removeItem("st_user");
      setUser(null);
      setPerms({});
    } finally {
      setLoading(false);
    }
  }, [loadPerms]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-fetch perms when window regains focus (admin may have changed them).
  useEffect(() => {
    if (!user) return;
    const onFocus = () => loadPerms();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, loadPerms]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Phase A (2026-08-20) — if the server requires MFA, surface the
    // pending token so LoginPage can render the code prompt without
    // completing the session yet.
    if (data.mfa_required) {
      return { mfaRequired: true, pendingToken: data.pending_token, email: data.user_email };
    }
    localStorage.setItem("st_token", data.access_token);
    localStorage.setItem("st_user", JSON.stringify(data.user));
    setUser(data.user);
    // Phase C — CSRF bootstrap after successful sign-in.
    try { await api.get("/auth/csrf"); } catch (_e) { /* warn-mode is fine */ }
    await loadPerms();
    return data.user;
  };

  // Phase A (2026-08-20) — verify TOTP or recovery code after step 1.
  const verifyMfa = async ({ pendingToken, otp, recoveryCode, rememberDevice }) => {
    const url = recoveryCode
      ? "/auth/mfa/verify-recovery"
      : "/auth/mfa/verify";
    const body = recoveryCode
      ? { code: recoveryCode, remember_device: !!rememberDevice }
      : { otp, remember_device: !!rememberDevice };
    const { data } = await api.post(url, body, {
      headers: { "X-MFA-Pending-Token": pendingToken },
    });
    localStorage.setItem("st_token", data.access_token);
    localStorage.setItem("st_user", JSON.stringify(data.user));
    setUser(data.user);
    // Phase C — bootstrap the CSRF cookie so subsequent POSTs pass the guard.
    try { await api.get("/auth/csrf"); } catch (_e) { /* warn-mode is fine */ }
    await loadPerms();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("auth/logout", err?.message || err);
    }
    localStorage.removeItem("st_token");
    localStorage.removeItem("st_user");
    setUser(null);
    setPerms({});
  };

  // Phase 19 helper — admin always returns true so the root account is
  // never accidentally locked out of any UI surface. Bug bundle #12
  // (2026-08-19): `full_access` override also short-circuits so users
  // granted Full Access see every gated nav item + pass every UI-side
  // permission check (matches the backend `assert_permission` bypass).
  const can = useCallback(
    (key) => {
      if (!user) return false;
      if (user.role === "system_admin") return true;
      if (user.full_access === true) return true;
      return !!perms[key];
    },
    [user, perms],
  );

  return (
    <AuthContext.Provider
      value={{ user, perms, loading, login, verifyMfa, logout, refresh, can, reloadPerms: loadPerms }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
